import { cn } from '@/lib/utils';

export const callGraphRootClass = (embedded: boolean) =>
  cn(
    'flex min-h-0 w-full flex-col overflow-hidden',
    embedded ? 'h-full flex-1' : 'h-[calc(100vh-8rem)] min-h-[480px]',
  );

export const callGraphToolbarClass =
  'flex shrink-0 flex-wrap items-center gap-2 border-0 border-b border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-3 py-2';

export const callGraphStatChipClass =
  'inline-flex items-center gap-1.5 rounded-full border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-2.5 py-1';

export const callGraphStatLabelClass =
  'text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-[var(--inv-text-muted)]';

export const callGraphStatValueClass =
  'font-tabular text-[0.8125rem] font-semibold leading-none text-[var(--inv-text)]';

export const callGraphToolbarHintClass =
  'm-0 ml-auto text-[0.75rem] text-[var(--inv-text-muted)] max-[900px]:ml-0 max-[900px]:w-full';

export const callGraphLayoutClass =
  'flex h-0 min-h-0 flex-1 items-stretch overflow-hidden max-[900px]:flex-col';

export const callGraphCanvasClass = (hasDetail: boolean) =>
  cn(
    'relative h-full min-h-0 min-w-0 flex-1 overflow-hidden bg-[#111827] p-0',
    hasDetail && 'max-[900px]:min-h-0',
  );

export const callGraphLegendClass =
  'absolute top-3 left-3 z-10 flex w-[11.5rem] flex-col gap-1.5 rounded-[10px] border border-solid border-white/20 bg-[rgba(8,14,32,0.92)] px-3 py-2.5 shadow-[0_8px_24px_rgba(0,0,0,0.28)] backdrop-blur-[6px]';

export const callGraphSearchWrapClass =
  'absolute top-3 right-3 z-10 w-[min(280px,calc(100%-1.5rem))]';

export const callGraphSearchFieldClass = 'relative flex items-center';

export const callGraphSearchInputClass =
  'h-9 border-white/20 bg-[rgba(8,14,32,0.92)] pr-8 text-[0.8rem] text-white shadow-none placeholder:text-slate-400 focus-visible:border-[var(--inv-accent)] focus-visible:ring-[var(--inv-accent-ring)]';

export const callGraphSearchClearClass =
  'absolute right-1.5 flex h-[22px] w-[22px] items-center justify-center rounded-md border-0 bg-white/10 p-0 text-base leading-none text-slate-100 transition-colors hover:bg-white/20 hover:text-white';

export const callGraphSearchResultsClass =
  'mt-1.5 max-h-[280px] overflow-y-auto rounded-[10px] border border-solid border-white/15 bg-[rgba(8,14,32,0.98)] shadow-[0_8px_24px_rgba(0,0,0,0.35)]';

export const callGraphSearchCountClass =
  'm-0 px-3 pb-1 pt-2 text-[0.7rem] text-slate-400';

export const callGraphSearchEmptyClass =
  'm-0 p-3 text-[0.8rem] text-slate-400';

export const callGraphSearchListClass = 'm-0 list-none p-1';

export const callGraphSearchItemClass = (active: boolean) =>
  cn(
    'flex h-auto w-full cursor-pointer flex-col items-start gap-0.5 whitespace-normal rounded-md border-0 bg-transparent px-2.5 py-2 text-left text-[0.78rem] text-slate-200 hover:bg-white/10',
    active && 'bg-white/10',
  );

export const callGraphSearchItemLabelClass =
  'flex items-center gap-1.5 font-semibold text-slate-50';

export const callGraphSearchItemPathClass =
  'break-all text-[0.68rem] leading-snug text-slate-400';

export const callGraphDetailsPanelClass =
  'flex h-full max-h-full min-h-0 w-[min(100%,22.5rem)] shrink-0 flex-col overflow-hidden border-0 border-l border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] max-[900px]:h-[42%] max-[900px]:max-h-[42%] max-[900px]:w-full max-[900px]:flex-none max-[900px]:border-l-0 max-[900px]:border-t';

export const callGraphDetailsHeaderClass =
  'flex shrink-0 items-start justify-between gap-2 border-0 border-b border-solid border-[var(--inv-border)] px-4 py-3';

export const callGraphDetailsBodyClass =
  'flex h-0 min-h-0 flex-1 flex-col overflow-hidden';

export const callGraphCallablesSectionClass =
  'min-h-0 max-h-[46%] shrink-0 overflow-y-auto overscroll-contain border-0 border-b border-solid border-[var(--inv-border)] px-3 py-3';

export const callGraphSourceSectionClass =
  'flex h-0 min-h-0 flex-1 flex-col overflow-hidden px-3 py-3';

export const callGraphSourceTitleClass =
  'm-0 mb-2 shrink-0 text-[0.75rem] font-semibold uppercase tracking-[0.06em] text-[var(--inv-text-muted)]';

export const callGraphSourceScrollClass =
  'h-0 min-h-0 flex-1 overflow-hidden';

export const callGraphCallableCardClass = (selected: boolean) =>
  cn(
    'flex h-auto w-full flex-col items-stretch gap-2 whitespace-normal rounded-[10px] border border-solid px-3 py-2.5 text-left shadow-none hover:translate-y-0',
    selected
      ? 'border-[var(--inv-accent)] bg-[var(--inv-accent-soft)]'
      : 'border-[var(--inv-border)] bg-[var(--inv-surface-muted)] hover:border-[var(--inv-border-strong)] hover:bg-[var(--inv-surface)]',
  );

export const callGraphScoreGridClass =
  'grid grid-cols-2 gap-1.5';

export const callGraphScoreChipClass =
  'flex min-w-0 items-center justify-between gap-1 rounded-md bg-[var(--inv-surface)] px-2 py-1';

export const callGraphBlastRadiusSectionClass =
  'mt-2 rounded-[8px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-2.5 py-2';

export const callGraphDepthSelectClass =
  'mt-1.5 h-8 w-full rounded-md border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-2 text-[0.75rem] font-medium text-[var(--inv-text)] shadow-none focus-visible:border-[var(--inv-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring)]';
